import { translate } from '../../../helper/translate';
import { useState, useEffect } from 'react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { setPageCacheHeaders } from '../../../helper/cache-control';
import { useRouter } from 'next/router';
import dynamic from "next/dynamic";
// import ReactPlayer from 'react-player';
const ReactPlayer = dynamic(() => import("react-player"), {
  ssr: false,
});
import styles from '../../../components/media/video.module.css';
import {
  getVideoList,
  getVideoDetail,
  getArticleList,
  getCaseStudyList,
  getWebinarList,
  getWhitePaperList,
} from '../../../helper';
import { VideoRes, ResourcesRes } from '../../../typescript/pages';
import { getRandomItems } from '../../../helper/utils';
import { getSimilarResourcesHybrid } from '../../../helper/algoliaSimilarity';
import BlogGrid from '../../../components/grids/blog-grid';
import {
  HeroBanner as HeroBannerProp,
  HeroBannerStyleButton,
  HeroTitle as HeroTitleProp,
} from '../../../typescript/component';
import HeroBanner from '../../../components/banners/hero-banner';
import HeroTitle from '../../../components/ui/hero-title';
import { SEO } from '../../../helper/seo';
import { cleanHtml } from '../../../sdk/utils';
import { HtmlBlock } from '../../../components/media/html-block';

// Live Preview imports
import ContentstackLivePreview from '@contentstack/live-preview-utils';
import { initLivePreview, initializeContentStackNewSdk } from '../../../contentstack-sdk/utils';
import { usePageState } from '../../../context/pageContext';

// SSG: generate props
export const getServerSideProps: GetServerSideProps<{
  video: VideoRes;
  locale: string;
  availableLocales: string[];
  similarResources: ResourcesRes[];
  source: 'algolia' | 'random' | 'mixed';
}> = async ({ params, locale, locales, res }) => {
  const availableLocales = [];
  const lang = locale || 'en-us';

  const videoId = params?.videoId as string;
  const video = await getVideoDetail({
    entryUrl: `/resources/video/${videoId}`,
    locale: lang
  });

  if (!video) return { notFound: true };

  if (locales && locales.length) {
    for (let l of locales) {
      const localeResource = await getVideoDetail({
        entryUrl: `/resources/video/${videoId}`,
        locale: l
      });

      if (localeResource) {
        availableLocales.push(l);
      }
    }
  }

  // Try to get similar resources from Algolia first
  let similarResources: ResourcesRes[] = [];
  let source: 'algolia' | 'random' | 'mixed' = 'random';

  try {
    const result = await getSimilarResourcesHybrid(
      {
        uid: video.uid,
        title: video.title,
        description: video.summary,
        keywords: video.seo?.keywords ? video.seo.keywords.split(',').map(k => k.trim()) : [],
        content: video.content,
        type: 'Video',
      },
      {
        locale: lang,
        maxResults: 3,
        excludeCurrentId: video.url,
      },
    );

    similarResources = result.resources;
    source = result.source;
  } catch (error) {
    // Silently fail and use fallback
  }

  // Fallback to random resources if Algolia fails or returns no results
  if (similarResources.length === 0) {
    const [articles, caseStudies, videos, webinars, whitepapers] = await Promise.all([
      getArticleList(lang),
      getCaseStudyList(lang),
      getVideoList(lang),
      getWebinarList(lang),
      getWhitePaperList(lang),
    ]);

    const allResources = [
      ...articles.flat(),
      ...caseStudies,
      ...videos,
      ...webinars,
      ...whitepapers,
    ];

    similarResources = getRandomItems(allResources, 3);
    source = 'random';
  }

  setPageCacheHeaders(res);

  return {
    props: { video, locale: lang, similarResources, source, availableLocales },
  };
};

export default function VideoPage({
  video,
  locale,
  similarResources,
  availableLocales,
  source,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [, setAvailableLocales] = usePageState('availableLocales');
  const [isPreview, setPreview] = useState(false);
  const [data, setData] = useState(video);
  const router = useRouter();
  if (router.isFallback) return <div>Loading...</div>;

  // Keep the shared page context in sync with this entry's locales, and clear it
  // on unmount so other pages don't inherit a stale list. Depending on the prop
  // matters because Next reuses this component when navigating between two
  // entries of the same type, so a mount-only effect would never re-run.
  useEffect(() => {
    setAvailableLocales(availableLocales ?? []);
    return () => setAvailableLocales([]);
  }, [availableLocales]);

  const heroBannerData: HeroBannerProp = {
    banner_eyebrow: translate('video', locale).toUpperCase(),
    banner_title: data.title,
    banner_title_alt: '',
    display_options: {
      align_image: 'Right',
      align_text: 'Left',
      background_color: 'Light',
      breadcrumb: true,
      show_button: false,
      split_50_50: true,
    },
    header_tag_type: 'H1',
    banner_media: data.featured_image,
    banner_media_alt: data.featured_image_alt,
    button: {
      link: '',
      title: '',
      style: HeroBannerStyleButton.SOLID,
    },
    anchor: '',
  };

  const heroTitleData: HeroTitleProp = {
    align_text: 'Left',
    bottom_border: true,
    content: '',
    eyebrow: translate('video', locale).toUpperCase(),
    header_tag_type: 'H1',
    show_breadcrumb: true,
    title: data.title,
  };

  const handlePlayerStart = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: window.scrollY }); // Prevent auto-scroll
    }
  };

  const getContent = async ({ stack }: {stack: any}) => {
    const page = await getVideoDetail({
      entryUrl: window.location.pathname,
      locale: locale,
      stackInstance: stack,
      clientSide: true
    });

    // Store data into state so we can dynamically update the fields
    setData(page);
  };

  useEffect(() => {
    setData(video);
  }, [video]);

  useEffect(() => {
    const stack = initializeContentStackNewSdk({ preview: true });

    initLivePreview({stackInstance: stack});
    ContentstackLivePreview.onEntryChange(() => {
      
      // It's loaded from the live preview so we want to finish up the setup
      if (stack.config.live_preview && stack.config.live_preview.live_preview) {
        setPreview(true);
        getContent({stack});
      }
    });
  }, []);

  return (
    <>
      <SEO
        meta_title={data?.seo?.meta_title}
        meta_description={data?.seo?.meta_description}
        keywords={data?.seo?.keywords}
        share_image={data?.featured_image?.url}
        currentLocale={locale}
        baseUrl={process.env.NEXT_PUBLIC_HOSTED_URL || ''}
        contentType="Video"
        youtube={data?.youtube}
        publishDate={data?.updated_at || undefined}
        modifiedDate={data?.updated_at || undefined}
      />
      <div className={styles.outerWrapper}>
        {data.featured_image?.url ? (
          <HeroBanner hero_banner={heroBannerData} />
        ) : (
          <HeroTitle hero_title={heroTitleData} />
        )}
        <div className={styles.moduleWrapper}>
          <div className={styles.root}>
            <section className={styles.virtualTour}>
              <HtmlBlock html={data.content} className={styles.description} />
            </section>
            <div className={styles.youtubeWrapper}>
              <ReactPlayer
                url={data.youtube}
                width="100%"
                height="100%"
                controls
                onPlay={handlePlayerStart}
              />
            </div>
          </div>
        </div>
        {similarResources && similarResources.length > 0 && (
          <div style={{ marginTop: '4rem', marginBottom: '4rem' }}>
            <BlogGrid
              resource_grid={{
                _metadata: {},
                uid: 'related-resources',
                component_type: 'Blog Grid',
                eyebrow: '',
                display_eyebrow: false,
                title: 'Related Resources',
                anchor: '',
                description: '',
                header_background: {
                  url: '',
                  uid: '',
                  title: '',
                  description: '',
                  filename: '',
                  content_type: '',
                },
                cta: { title: '', url: '' },
                display_cta: false,
                featured_resources: similarResources,
                case_studies: [],
                auto_resources: false,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
